// Queries and mutations for the library, on TanStack Query. Every mutation
// invalidates what it touched; the list detail carries server totals.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gear, type GearItem, type GearList, type GearListItem } from "../../api/client";

export const keys = {
  items: ["items"] as const,
  lists: ["lists"] as const,
  list: (id: string) => ["list", id] as const,
};

export function useItems() {
  return useQuery({ queryKey: keys.items, queryFn: gear.items });
}
export function useLists() {
  return useQuery({ queryKey: keys.lists, queryFn: gear.lists });
}
export function useListDetail(id: string | null) {
  return useQuery({ queryKey: keys.list(id ?? ""), queryFn: () => gear.list(id!), enabled: !!id });
}

export function useGearMutations() {
  const qc = useQueryClient();
  const all = () => {
    qc.invalidateQueries({ queryKey: keys.items });
    qc.invalidateQueries({ queryKey: keys.lists });
    qc.invalidateQueries({ queryKey: ["list"] });
  };
  return {
    createItem: useMutation({ mutationFn: (item: Partial<GearItem>) => gear.createItem(item), onSuccess: all }),
    createItems: useMutation({ mutationFn: (items: Partial<GearItem>[]) => gear.createItems(items), onSuccess: all }),
    updateItem: useMutation({ mutationFn: ({ id, ...item }: Partial<GearItem> & { id: string }) => gear.updateItem(id, item), onSuccess: all }),
    deleteItem: useMutation({ mutationFn: (id: string) => gear.deleteItem(id), onSuccess: all }),
    createList: useMutation({ mutationFn: (list: Partial<GearList>) => gear.createList(list), onSuccess: all }),
    updateList: useMutation({ mutationFn: ({ id, ...list }: Partial<GearList> & { id: string }) => gear.updateList(id, list), onSuccess: all }),
    deleteList: useMutation({ mutationFn: (id: string) => gear.deleteList(id), onSuccess: all }),
    addLine: useMutation({
      mutationFn: ({ listId, ...line }: { listId: string; item_id: string; quantity?: number }) => gear.addLine(listId, line),
      onSuccess: all,
    }),
    updateLine: useMutation({
      mutationFn: ({ id, ...line }: Pick<GearListItem, "id" | "quantity" | "worn" | "checked" | "consumable">) => gear.updateLine(id, line),
      onSuccess: all,
    }),
    deleteLine: useMutation({ mutationFn: (id: string) => gear.deleteLine(id), onSuccess: all }),
  };
}
